import { HttpTypes } from "@medusajs/types"
import { t } from "i18next"
import { Outlet, RouteObject, UIMatch } from "react-router-dom"
import { ProtectedRoute } from "../../components/authentication/protected-route"
import { MainLayout } from "../../components/layout/main-layout"
import { PublicLayout } from "../../components/layout/public-layout"
import { SettingsLayout } from "../../components/layout/settings-layout"
import { ErrorBoundary } from "../../components/utilities/error-boundary"

export function getRouteMap({
  settingsRoutes,
  coreRoutes,
}: {
  settingsRoutes: RouteObject[]
  coreRoutes: RouteObject[]
}) {
  return [
    {
      element: <ProtectedRoute />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          element: <MainLayout />,
          children: [
            {
              path: "/",
              errorElement: <ErrorBoundary />,
              lazy: () => import("../../routes/home"),
            },
            // Tags Management
            {
              path: "/tags",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => "Tags",
              },
              children: [
                {
                  path: "",
                  lazy: () => import("../../routes/tags/tag-list/index"),
                  children: [
                    {
                      path: "create",
                      lazy: () => import("../../routes/tags/tag-create/index"),
                    },
                  ],
                },
                {
                  path: "create",
                  lazy: () => import("../../routes/tags/tag-create/index"),
                },
                {
                  path: ":id",
                  lazy: () => import("../../routes/tags/tag-edit/index"),
                },
              ],
            },
            // Types Management
            {
              path: "/types",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => "Types",
              },
              children: [
                {
                  path: "",
                  lazy: () => import("../../routes/types/type-list/index"),
                  children: [
                    {
                      path: "create",
                      lazy: () => import("../../routes/types/type-create/index"),
                    },
                  ],
                },
                {
                  path: ":id",
                  lazy: () => import("../../routes/types/type-edit/index"),
                },
              ],
            },
            // Packaging Management
            {
              path: "/packaging",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => "Packaging",
              },
              children: [
                {
                  path: "",
                  lazy: () => import("../../routes/packaging/packaging-list/index"),
                  children: [
                    {
                      path: "create",
                      lazy: () => import("../../routes/packaging/packaging-create/index"),
                    },
                  ],
                },
                {
                  path: ":id",
                  lazy: () => import("../../routes/packaging/packaging-edit/index"),
                },
              ],
            },
            // Sizes Management
            {
              path: "/sizes",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => "Sizes",
              },
              children: [
                {
                  path: "",
                  lazy: () => import("../../routes/sizes/size-list/index"),
                  children: [
                    {
                      path: "create",
                      lazy: () => import("../../routes/sizes/size-create/index"),
                    },
                  ],
                },
                {
                  path: ":id",
                  lazy: () => import("../../routes/sizes/size-edit/index"),
                },
              ],
            },
            // Promotions Management
            {
              path: "/promotions",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => "Promotions",
              },
              children: [
                {
                  path: "",
                  lazy: () => import("../../routes/promotions/promotion-list"),
                  children: [
                    {
                      path: "create",
                      lazy: () => import("../../routes/promotions/promotion-create"),
                    },
                  ],
                },
              ],
            },
            // Products Management
            {
              path: "/products",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => t("products.domain"),
              },
              children: [
                {
                  path: "",
                  lazy: () => import("../../routes/products/product-list"),
                  children: [
                    {
                      path: "create",
                      lazy: () =>
                        import("../../routes/products/product-create"),
                    },
                  ],
                },
                {
                  path: ":id",
                  errorElement: <ErrorBoundary />,
                  lazy: async () => {
                    const { Breadcrumb, loader } = await import(
                      "../../routes/products/product-detail"
                    )

                    return {
                      Component: Outlet,
                      loader,
                      handle: {
                        breadcrumb: (
                          match: UIMatch<HttpTypes.AdminProductResponse>
                        ) => <Breadcrumb {...match} />,
                      },
                    }
                  },
                  children: [
                    {
                      path: "",
                      lazy: () =>
                        import("../../routes/products/product-detail"),
                      children: [
                        {
                          path: "edit",
                          lazy: () =>
                            import("../../routes/products/product-edit"),
                        },
                        {
                          path: "media",
                          lazy: () =>
                            import("../../routes/products/product-media"),
                        },
                        {
                          path: "organization",
                          lazy: () =>
                            import(
                              "../../routes/products/product-organization"
                            ),
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            // Categories Management
            {
              path: "/categories",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => "Catégories",
              },
              children: [
                {
                  path: "",
                  lazy: () =>
                    import("../../routes/categories/category-list"),
                  children: [
                    {
                      path: "create",
                      lazy: () =>
                        import("../../routes/categories/category-create"),
                    },
                  ],
                },
                {
                  path: ":id",
                  lazy: async () => {
                    const { Component, Breadcrumb, loader } = await import(
                      "../../routes/categories/category-detail"
                    )

                    return {
                      Component,
                      loader,
                      handle: {
                        breadcrumb: (
                          match: UIMatch<HttpTypes.AdminProductCategoryResponse>
                        ) => <Breadcrumb {...match} />,
                      },
                    }
                  },
                  children: [
                    {
                      path: "edit",
                      lazy: () =>
                        import("../../routes/categories/category-edit"),
                    },
                    {
                      path: "products",
                      lazy: () =>
                        import(
                          "../../routes/categories/category-add-products"
                        ),
                    },
                  ],
                },
              ],
            },
            // Collections Management (renamed from Ranges)
            {
              path: "/collections",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => t("collections.domain"),
              },
              children: [
                {
                  path: "",
                  lazy: () =>
                    import("../../routes/collections/collection-list"),
                  children: [
                    {
                      path: "create",
                      lazy: () =>
                        import("../../routes/collections/collection-create"),
                    },
                  ],
                },
                {
                  path: ":id",
                  lazy: async () => {
                    const { Component, Breadcrumb, loader } = await import(
                      "../../routes/collections/collection-detail"
                    )

                    return {
                      Component,
                      loader,
                      handle: {
                        breadcrumb: (
                          match: UIMatch<HttpTypes.AdminCollectionResponse>
                        ) => <Breadcrumb {...match} />,
                      },
                    }
                  },
                  children: [
                    {
                      path: "edit",
                      lazy: () =>
                        import("../../routes/collections/collection-edit"),
                    },
                    {
                      path: "products",
                      lazy: () =>
                        import(
                          "../../routes/collections/collection-add-products"
                        ),
                    },
                  ],
                },
              ],
            },
            // Orders Management
            {
              path: "/orders",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => t("orders.domain"),
              },
              children: [
                {
                  path: "",
                  lazy: () => import("../../routes/orders/order-list"),
                },
                {
                  path: ":id",
                  lazy: async () => {
                    const { Component, Breadcrumb, loader } = await import(
                      "../../routes/orders/order-detail"
                    )

                    return {
                      Component,
                      loader,
                      handle: {
                        breadcrumb: (
                          match: UIMatch<HttpTypes.AdminOrderResponse>
                        ) => <Breadcrumb {...match} />,
                      },
                    }
                  },
                  children: [
                    {
                      path: "email",
                      lazy: () =>
                        import("../../routes/orders/order-edit-email"),
                    },
                    {
                      path: "shipping-address",
                      lazy: () =>
                        import(
                          "../../routes/orders/order-edit-shipping-address"
                        ),
                    },
                    {
                      path: "billing-address",
                      lazy: () =>
                        import(
                          "../../routes/orders/order-edit-billing-address"
                        ),
                    },
                  ],
                },
              ],
            },
            // Customers Management
            {
              path: "/customers",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => t("customers.domain"),
              },
              children: [
                {
                  path: "",
                  lazy: () => import("../../routes/customers/customer-list"),
                  children: [
                    {
                      path: "create",
                      lazy: () =>
                        import("../../routes/customers/customer-create"),
                    },
                  ],
                },
                {
                  path: ":id",
                  lazy: async () => {
                    const { Component, Breadcrumb, loader } = await import(
                      "../../routes/customers/customer-detail"
                    )

                    return {
                      Component,
                      loader,
                      handle: {
                        breadcrumb: (
                          match: UIMatch<HttpTypes.AdminCustomerResponse>
                        ) => <Breadcrumb {...match} />,
                      },
                    }
                  },
                  children: [
                    {
                      path: "edit",
                      lazy: () =>
                        import("../../routes/customers/customer-edit"),
                    },
                    {
                      path: "create-address",
                      lazy: () =>
                        import(
                          "../../routes/customers/customer-create-address"
                        ),
                    },
                  ],
                },
              ],
            },
            // Video Ads (Homepage Video)
            {
              path: "/video-ads",
              errorElement: <ErrorBoundary />,
              handle: {
                breadcrumb: () => "Video Ads",
              },
              lazy: () => import("../../routes/video-ads"),
            },
            // Add core extension routes
            ...coreRoutes,
          ],
        },
        // Settings Layout
        {
          path: "/settings",
          element: <SettingsLayout />,
          children: [
            {
              path: "",
              lazy: () => import("../../routes/settings"),
            },
            {
              // Removed homepage settings route in favor of top-level Video Ads
            },
            {
              path: "profile",
              lazy: () => import("../../routes/profile"),
              children: [
                {
                  path: "edit",
                  lazy: () => import("../../routes/profile/profile-edit"),
                },
              ],
            },
            {
              path: "store",
              lazy: () => import("../../routes/store"),
              children: [
                {
                  path: "contact",
                  lazy: () => import("../../routes/store/store-edit-contact"),
                },
                {
                  path: "phones",
                  lazy: () => import("../../routes/store/store-edit-phones"),
                },
                {
                  path: "social",
                  lazy: () => import("../../routes/store/store-edit-social"),
                },
              ],
            },
            {
              path: "users",
              lazy: () => import("../../routes/users"),
              children: [
                {
                  path: "create",
                  lazy: () => import("../../routes/users/user-create"),
                },
              ],
            },
            // Add settings extension routes
            ...settingsRoutes,
          ],
        },
      ],
    },
    // Public routes
    {
      element: <PublicLayout />,
      children: [
        {
          path: "/login",
          lazy: () => import("../../routes/login"),
        },
        {
          path: "/reset-password",
          lazy: () => import("../../routes/reset-password"),
        },
        {
          path: "/invite",
          lazy: () => import("../../routes/invite"),
        },
      ],
    },
    // Catch-all route
    {
      path: "*",
      lazy: () => import("../../routes/no-match"),
    },
  ]
}